import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Typography,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemButton,
  CircularProgress,
  Collapse,
  Alert,
  Snackbar,
  Link,
  Skeleton,
} from '@mui/material';
import { ExpandMore, ChevronRight } from '@mui/icons-material';
import SquareIcon from '@mui/icons-material/Square';
import ExploreIcon from '@mui/icons-material/Explore';
import FolderCopyIcon from '@mui/icons-material/FolderCopy';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import GenericModal from './GenericModal';
import FileSystemTree from './FileSystemTree';
import { quoteService, fileSystemService } from '../services/api';
import { QuoteOfTheDay, FileSystemMetadata, FileSystemNode } from '../types/api';

// Unique IDs for each expandable section in the backpack
// These IDs are used in URL state encoding to preserve which sections are expanded
// when navigating to/from the editor
const SECTION_IDS = {
  DOCUMENTS: 'docs',
  LITERACY: 'lit',
  BODY_COMP: 'body',
  BRAIN_DIET: 'brain',
  DEPRECATED: 'dep',
} as const;

// Type for backpack state that will be encoded in URL
interface BackpackState {
  from: 'backpack';
  expanded: string[]; // Array of section IDs that are expanded
  nestedExpanded?: string[]; // Array of nested section IDs (e.g., brain diet, deprecated)
  expandedFolders?: string[]; // Array of folder node IDs that are expanded in FileSystemTree
  basePath?: string; // The path the backpack was opened from (without /pack suffix)
}

interface BackpackModalProps {
  open: boolean;
  onClose: () => void;
  // Optional initial state for restoring expanded sections when navigating back
  initialExpandedSections?: string[];
  initialNestedExpanded?: string[];
  initialExpandedFolders?: string[]; // Folder IDs that should be expanded in FileSystemTree
}

const BackpackModal: React.FC<BackpackModalProps> = ({ 
  open, 
  onClose, 
  initialExpandedSections = [],
  initialNestedExpanded = [],
  initialExpandedFolders = [],
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [quote, setQuote] = useState<QuoteOfTheDay | null>(null);
  const [quoteLoading, setQuoteLoading] = useState(false);
  const [quoteError, setQuoteError] = useState<string | null>(null);
  
  const [fileSystem, setFileSystem] = useState<FileSystemMetadata | null>(null);
  const [fsLoading, setFsLoading] = useState(false);
  const [fsError, setFsError] = useState<string | null>(null);
  const [snackbarMessage, setSnackbarMessage] = useState<string | null>(null);
  
  // Track which folders are expanded in the FileSystemTree
  // This is updated when user expands/collapses folders and used when navigating to editor
  const [expandedFolders, setExpandedFolders] = useState<string[]>(initialExpandedFolders);
  
  // Flag to track if we're currently restoring state (to skip animations)
  const [isRestoringState, setIsRestoringState] = useState(
    initialExpandedSections.length > 0 || 
    initialNestedExpanded.length > 0 || 
    initialExpandedFolders.length > 0
  );
  
  // Track which sections are expanded - can be initialized from props when navigating back
  const [filesExpanded, setFilesExpanded] = useState(initialExpandedSections.includes(SECTION_IDS.DOCUMENTS));
  const [textResourcesExpanded, setTextResourcesExpanded] = useState(initialExpandedSections.includes(SECTION_IDS.LITERACY));
  const [bodyCompExpanded, setBodyCompExpanded] = useState(initialExpandedSections.includes(SECTION_IDS.BODY_COMP));
  const [brainDietExpanded, setBrainDietExpanded] = useState(initialNestedExpanded.includes(SECTION_IDS.BRAIN_DIET));
  const [deprecatedExpanded, setDeprecatedExpanded] = useState(initialNestedExpanded.includes(SECTION_IDS.DEPRECATED));
  const [traversingDaoExpanded, setTraversingDaoExpanded] = useState(false);
  
  // Track previous open state to detect when modal opens fresh (using ref to avoid effect loop)
  const prevOpenRef = useRef(open);

  // Effect to restore expanded sections when props change (e.g., when navigating back from editor)
  // This is necessary because useState only uses initial values on mount, not when props update
  useEffect(() => {
    const hasInitialState = initialExpandedSections.length > 0 || 
                           initialNestedExpanded.length > 0 || 
                           initialExpandedFolders.length > 0;
    
    if (hasInitialState) {
      // Restoring state from navigation (user came from editor)
      setIsRestoringState(true);
      setFilesExpanded(initialExpandedSections.includes(SECTION_IDS.DOCUMENTS));
      setTextResourcesExpanded(initialExpandedSections.includes(SECTION_IDS.LITERACY));
      setBodyCompExpanded(initialExpandedSections.includes(SECTION_IDS.BODY_COMP));
      setBrainDietExpanded(initialNestedExpanded.includes(SECTION_IDS.BRAIN_DIET));
      setDeprecatedExpanded(initialNestedExpanded.includes(SECTION_IDS.DEPRECATED));
      setExpandedFolders(initialExpandedFolders);
      
      // Clear the restoring flag after a brief moment to allow animations on future interactions
      setTimeout(() => setIsRestoringState(false), 100);
    } else if (open && !prevOpenRef.current && !hasInitialState) {
      // Modal just opened fresh (not from editor) - reset all expanded state
      // This ensures a clean slate when user manually opens backpack
      setFilesExpanded(false);
      setTextResourcesExpanded(false);
      setBodyCompExpanded(false);
      setBrainDietExpanded(false);
      setDeprecatedExpanded(false);
      setExpandedFolders([]);
    }
    
    // Update previous open state ref
    prevOpenRef.current = open;
  }, [open, initialExpandedSections, initialNestedExpanded, initialExpandedFolders]);

  useEffect(() => {
    // Fetch quote and file system when modal opens
    if (open) {
      fetchQuote();
      fetchFileSystem();
    }
  }, [open]);

  const fetchQuote = async () => {
    setQuoteLoading(true);
    setQuoteError(null);
    try {
      const response = await quoteService.getQuoteOfTheDay();
      if (response.success && response.data) {
        setQuote(response.data);
      } else {
        const isNetworkError = response.error?.toLowerCase().includes('network');
        setQuoteError(isNetworkError ? '' : (response.error || 'Failed to load quote'));
      }
    } catch (err) {
      setQuoteError('');
    } finally {
      setQuoteLoading(false);
    }
  };

  const fetchFileSystem = async (showLoading = true) => {
    if (showLoading) {
      setFsLoading(true);
    }
    setFsError(null);
    try {
      const response = await fileSystemService.getFileSystem();
      if (response.success && response.data) {
        setFileSystem(response.data);
      } else {
        setFsError(response.error || 'Failed to load file system');
      }
    } catch (err) {
      setFsError('Failed to load file system');
    } finally {
      if (showLoading) {
        setFsLoading(false);
      }
    }
  };

  const handleFileClick = (file: FileSystemNode) => {
    // Build the current backpack state to preserve when navigating to editor
    const expandedSections: string[] = [];
    const nestedExpanded: string[] = [];
    
    // Collect which top-level sections are currently expanded
    if (filesExpanded) expandedSections.push(SECTION_IDS.DOCUMENTS);
    if (textResourcesExpanded) expandedSections.push(SECTION_IDS.LITERACY);
    if (bodyCompExpanded) expandedSections.push(SECTION_IDS.BODY_COMP);
    
    // Collect which nested sections are expanded (within Literacy)
    if (brainDietExpanded) nestedExpanded.push(SECTION_IDS.BRAIN_DIET);
    if (deprecatedExpanded) nestedExpanded.push(SECTION_IDS.DEPRECATED);
    
    // Create state object with section IDs (not human-readable names)
    // Include which folders in the FileSystemTree are currently expanded
    // basePath is the route to return to when coming back from editor/viewer
    const basePath = location.pathname.endsWith('/pack')
      ? location.pathname.slice(0, -5)
      : location.pathname;
    const backpackState: BackpackState = {
      from: 'backpack',
      expanded: expandedSections,
      ...(nestedExpanded.length > 0 && { nestedExpanded }),
      ...(expandedFolders.length > 0 && { expandedFolders }),
      basePath,
    };
    
    // Encode state as Base64 to keep URL clean and handle special characters
    const encodedState = btoa(JSON.stringify(backpackState));
    
    // Store backpack state in sessionStorage so it persists across browser back button
    // This allows restoration whether user clicks app back button OR browser back button
    sessionStorage.setItem('backpackState', encodedState);
    
    // Check file extension to determine how to open it
    const fileExtension = file.name.split('.').pop()?.toLowerCase();
    const imageExtensions = ['jpg', 'jpeg', 'png'];
    
    if (imageExtensions.includes(fileExtension || '')) {
      // For image files, navigate to viewer page.
      // replace: true prevents the viewer from accumulating in the history stack —
      // if the user presses browser-back from within the viewer (or after returning
      // via the app back button), they can never land on a stale viewer page.
      navigate(`/viewer/${file.id}?state=${encodedState}`, { replace: true });
    } else {
      // For text files (.md, .txt), navigate to editor.
      // replace: true prevents the editor from accumulating in the history stack —
      // if the user presses browser-back from within the editor (or after returning
      // via the app back button), they can never land on a stale editor page.
      navigate(`/editor/${file.id}?state=${encodedState}`, { replace: true });
    }
  };

  const handleCreateFile = async (parentId: string, name: string) => {
    try {
      const response = await fileSystemService.createFile({ name, parentId, content: '' });
      if (response.success) {
        await fetchFileSystem(false);
        setSnackbarMessage(`File "${name}" created successfully`);
      } else {
        setSnackbarMessage(`Error: ${response.error}`);
      }
    } catch (err) {
      setSnackbarMessage('Failed to create file');
    }
  };

  const handleCreateFolder = async (parentId: string, name: string) => {
    try {
      const response = await fileSystemService.createFolder({ name, parentId });
      if (response.success) {
        await fetchFileSystem(false);
        setSnackbarMessage(`Folder "${name}" created successfully`);
      } else {
        setSnackbarMessage(`Error: ${response.error}`);
      }
    } catch (err) {
      setSnackbarMessage('Failed to create folder');
    }
  };

  const handleUploadFile = async (parentId: string, file: File) => {
    try {
      const response = await fileSystemService.uploadFile({ parentId, file });
      if (response.success) {
        await fetchFileSystem(false);
        setSnackbarMessage(`File "${file.name}" uploaded successfully`);
      } else {
        setSnackbarMessage(`Error: ${response.error}`);
      }
    } catch (err) {
      setSnackbarMessage('Failed to upload file');
    }
  };

  const handleDeleteNode = async (nodeId: string) => {
    try {
      const node = fileSystem?.nodes[nodeId];
      const isFile = node?.type === 'file';
      
      const response = isFile
        ? await fileSystemService.deleteFile(nodeId)
        : await fileSystemService.deleteFolder(nodeId);
      
      if (response.success) {
        await fetchFileSystem(false);
        setSnackbarMessage(`${isFile ? 'File' : 'Folder'} deleted successfully`);
      } else {
        setSnackbarMessage(`Error: ${response.error}`);
      }
    } catch (err) {
      setSnackbarMessage('Failed to delete');
    }
  };

  const handleRenameNode = async (nodeId: string, newName: string) => {
    try {
      const node = fileSystem?.nodes[nodeId];
      const isFile = node?.type === 'file';
      
      const response = isFile
        ? await fileSystemService.updateFile(nodeId, { name: newName })
        : await fileSystemService.updateFolder(nodeId, { name: newName });
      
      if (response.success) {
        await fetchFileSystem(false);
        setSnackbarMessage('Renamed successfully');
      } else {
        setSnackbarMessage(`Error: ${response.error}`);
      }
    } catch (err) {
      setSnackbarMessage('Failed to rename');
    }
  };

  const handleMoveNode = async (nodeId: string, newParentId: string, newOrder: number) => {
    try {
      const response = await fileSystemService.moveNode(nodeId, { parentId: newParentId, order: newOrder });
      if (response.success) {
        await fetchFileSystem(false);
        setSnackbarMessage('Moved successfully');
      } else {
        setSnackbarMessage(`Error: ${response.error}`);
      }
    } catch (err) {
      setSnackbarMessage('Failed to move');
    }
  };

  return (
    <>
      <GenericModal 
        open={open} 
        onClose={onClose}
        maxWidth={800}
        ariaLabelledBy="backpack-modal-title"
        ariaDescribedBy="backpack-modal-description"
      >
        <Box>
          <Typography 
            id="backpack-modal-title"
            variant="h4" 
            sx={{ fontWeight: 600, mb: 3, pr: 6 }}
          >
            🎒 Backpack
          </Typography>
          
          <Typography 
            variant="body1" 
            color="text.secondary" 
            sx={{ mb: 2 }}
          >
            Items:
          </Typography>
          
          <List sx={{ pl: 1 }}>
            {/* Main Quest - Web Link */}
            <ListItem 
              disablePadding
              sx={{ 
                mb: 1,
                pl: 0,
              }}
            >
              <ListItemButton 
                onClick={() => navigate('/history/stats/38BVMOp50CJualXd5s9oRScWxo7')}
                sx={{ pr: 2 }}
              >
                <ListItemIcon sx={{ minWidth: 32, display: 'flex', justifyContent: 'center', marginLeft: '-0.375rem' }}>
                  <SquareIcon sx={{ fontSize: 8, transform: 'rotate(45deg)' }} />
                </ListItemIcon>
                <ListItemIcon sx={{ minWidth: 32, color: 'primary.main' }}>
                  <ExploreIcon />
                </ListItemIcon>
                <ListItemText 
                  primary="Main Quest"
                  primaryTypographyProps={{
                    variant: 'body1',
                    sx: { lineHeight: 1.6, fontWeight: 500, color: 'primary.main' }
                  }}
                />
              </ListItemButton>
            </ListItem>

            {/* Documents and Files - Expandable */}
            <ListItem 
              disablePadding
              sx={{ 
                mb: 1,
                pl: 0,
                flexDirection: 'column',
                alignItems: 'stretch',
              }}
            >
              <ListItemButton 
                onClick={() => setFilesExpanded(!filesExpanded)}
                sx={{ pr: 2 }}
              >
                <ListItemIcon sx={{ minWidth: 32 }}>
                  {filesExpanded ? <ExpandMore /> : <ChevronRight />}
                </ListItemIcon>
                <ListItemIcon sx={{ minWidth: 32, color: 'primary.main' }}>
                  <FolderCopyIcon />
                </ListItemIcon>
                <ListItemText 
                  primary="Documents and Files"
                  primaryTypographyProps={{
                    variant: 'body1',
                    sx: { lineHeight: 1.6, fontWeight: 500 }
                  }}
                />
              </ListItemButton>
              
              <Collapse 
                in={filesExpanded} 
                timeout={isRestoringState ? 0 : 'auto'} 
                unmountOnExit
              >
                <Box sx={{ 
                  pl: 4, 
                  pr: 2, 
                  pt: 2, 
                  pb: 2,
                  bgcolor: 'background.default',
                  borderRadius: 1,
                  mt: 1,
                  maxHeight: '400px',
                  overflowY: 'auto',
                }}>
                  {fsError ? (
                    <Alert severity="error" sx={{ mb: 2 }}>
                      {fsError}
                    </Alert>
                  ) : isRestoringState && expandedFolders.length > 0 ? (
                    // Show skeleton loaders while restoring folder state
                    <Box>
                      {expandedFolders.map((folderId, index) => (
                        <Box key={folderId} sx={{ mb: 2 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                            <Skeleton variant="circular" width={24} height={24} sx={{ mr: 1 }} />
                            <Skeleton variant="text" width={`${60 + (index * 10) % 40}%`} height={32} />
                          </Box>
                          {/* Nested file skeletons */}
                          <Box sx={{ pl: 4 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                              <Skeleton variant="circular" width={20} height={20} sx={{ mr: 1 }} />
                              <Skeleton variant="text" width={`${50 + (index * 15) % 30}%`} height={28} />
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                              <Skeleton variant="circular" width={20} height={20} sx={{ mr: 1 }} />
                              <Skeleton variant="text" width={`${55 + (index * 12) % 25}%`} height={28} />
                            </Box>
                          </Box>
                        </Box>
                      ))}
                    </Box>
                  ) : (
                    <FileSystemTree
                      fileSystem={fileSystem}
                      loading={fsLoading}
                      onFileClick={handleFileClick}
                      onCreateFile={handleCreateFile}
                      onCreateFolder={handleCreateFolder}
                      onUploadFile={handleUploadFile}
                      onDeleteNode={handleDeleteNode}
                      onRenameNode={handleRenameNode}
                      onMoveNode={handleMoveNode}
                      initialExpandedFolders={expandedFolders}
                      onExpandedFoldersChange={setExpandedFolders}
                    />
                  )}
                </Box>

              </Collapse>
            </ListItem>

            {/* Traversing the Dao - Expandable */}
            <ListItem
              disablePadding
              sx={{
                mb: 1,
                pl: 0,
                flexDirection: 'column',
                alignItems: 'stretch',
              }}
            >
              <ListItemButton
                onClick={() => setTraversingDaoExpanded(!traversingDaoExpanded)}
                sx={{ pr: 2 }}
              >
                <ListItemIcon sx={{ minWidth: 32 }}>
                  {traversingDaoExpanded ? <ExpandMore /> : <ChevronRight />}
                </ListItemIcon>
                <ListItemIcon sx={{ minWidth: 32, color: 'primary.main' }}>
                  <AccessTimeIcon />
                </ListItemIcon>
                <ListItemText
                  primary="Traversing the Dao"
                  primaryTypographyProps={{
                    variant: 'body1',
                    sx: { lineHeight: 1.6, fontWeight: 500 }
                  }}
                />
              </ListItemButton>

              <Collapse in={traversingDaoExpanded} timeout="auto" unmountOnExit>
                <Box sx={{ pl: 4, pr: 2, pt: 2, pb: 2, bgcolor: 'background.default', borderRadius: 1, mt: 1 }}>
                  <Typography variant="body2" sx={{ lineHeight: 1.6, mb: 0.5, fontWeight: 700 }}>Stratagems:</Typography>
                  <Box component="ul" sx={{ mt: 0, mb: 0, pl: 3 }}>
                    <Box component="li">
                      <Typography variant="body2" sx={{ lineHeight: 1.6 }}>Pomo 90/30, 35/10, 25/5</Typography>
                    </Box>
                    <Box component="li">
                      <Typography variant="body2" sx={{ lineHeight: 1.6 }}>35/10 pomo—either nap on the 10 or switch to a task that is low-energy</Typography>
                    </Box>
                  </Box>
                  <Typography variant="body2" sx={{ lineHeight: 1.6, mt: 1, fontWeight: 700 }}>Brain Diet: <Link href="obsidian://open?vault=My%20First%20Remote%20Vault&file=Brain%20Diet" variant="body2" sx={{ color: 'primary.main' }}>Obsidian</Link></Typography>
                </Box>
              </Collapse>
            </ListItem>


            {/* Body Comp - Expandable */}
            <ListItem 
              disablePadding
              sx={{ 
                mb: 1,
                pl: 0,
                flexDirection: 'column',
                alignItems: 'stretch',
              }}
            >
              <ListItemButton 
                onClick={() => setBodyCompExpanded(!bodyCompExpanded)}
                sx={{ pr: 2 }}
              >
                <ListItemIcon sx={{ minWidth: 32 }}>
                  {bodyCompExpanded ? <ExpandMore /> : <ChevronRight />}
                </ListItemIcon>
                <ListItemIcon sx={{ minWidth: 32, color: 'success.main' }}>
                  <TrendingDownIcon />
                </ListItemIcon>
                <ListItemText 
                  primary="current body comp goal - DECREASE fat % until measure as 15%"
                  primaryTypographyProps={{
                    variant: 'body1',
                    sx: { lineHeight: 1.6, fontWeight: 500 }
                  }}
                />
              </ListItemButton>
              
              <Collapse 
                in={bodyCompExpanded} 
                timeout={isRestoringState ? 0 : 'auto'} 
                unmountOnExit
              >
                <Box sx={{ 
                  pl: 4, 
                  pr: 2, 
                  pt: 2, 
                  pb: 2,
                  bgcolor: 'background.default',
                  borderRadius: 1,
                  mt: 1,
                }}>
                  <Typography variant="body2" sx={{ lineHeight: 1.6 }}>
                    current body comp goal - DECREASE fat % until measure as 15%. Need to keep body comp between 15 and 18 percent body fat

                    <br></br>
                      <br></br>
                    For body fat what I could do is if I ever hit 18 get back down to 17 (gradually) and if I ever hit 16 get back up to 17 (gradually) and beyond that just maintain. (if i think i look better slightly lower, i could do the same between 15 and 17). Consider that body fat % does impact face fat which significantly impacts perceived attractiveness, which is power and may have an impact on my career and life's work.
                    <br></br>
                    <br></br>
                    <Link href="http://localhost:3000/editor/3Dk8mjjzsdk1HtkaUmSn3kBWAFO?state=eyJmcm9tIjoiYmFja3BhY2siLCJleHBhbmRlZCI6WyJkb2NzIl0sImV4cGFuZGVkRm9sZGVycyI6WyIzNkFpYzlDMFd1TDJUQjZZZ2dqTDAzcExjNDciLCIzN2x1aGNHTzJKRGxWazJMazVNa1ZiR0ZhdmciLCIzRGszbU9CT0U3dDVrbjF0S090S2pBejBmMmEiLCIzOHVtRXZjUENKekl4NDNkVE9pQXF4YXl0Y2YiXSwiYmFzZVBhdGgiOiIvdG9kYXkvcGFzb3MifQ==" variant="body2" sx={{ color: 'primary.main' }}>Spiritual Relationship with Food</Link> ☯️
                  </Typography>
                </Box>
              </Collapse>
            </ListItem>
          </List>
          
          <Box 
            sx={{ 
              mt: 4, 
              p: 2, 
              bgcolor: 'primary.main', 
              borderRadius: 1,
              color: 'primary.contrastText'
            }}
          >
            <Typography variant="body2" sx={{ fontWeight: 500, mb: 1 }}>
              💡 QOTD:
            </Typography>
            {quoteLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                <CircularProgress size={24} sx={{ color: 'primary.contrastText' }} />
              </Box>
            ) : quoteError ? (
              <Typography variant="body2" sx={{ fontStyle: 'italic', opacity: 0.9 }}>
                {quoteError}
              </Typography>
            ) : quote ? (
              <>
                <Typography variant="body1" sx={{ fontStyle: 'italic', mb: 1 }}>
                  "{quote.quote}"
                </Typography>
                <Typography variant="body2" sx={{ textAlign: 'right', opacity: 0.9 }}>
                  — {quote.author}
                </Typography>
              </>
            ) : null}
          </Box>
        </Box>
      </GenericModal>

      {/* Snackbar for notifications */}
      <Snackbar
        open={Boolean(snackbarMessage)}
        autoHideDuration={4000}
        onClose={() => setSnackbarMessage(null)}
        message={snackbarMessage}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        ContentProps={{
          sx: {
            backgroundColor: '#2A2A2A',
            color: '#32CA1D',
            fontWeight: 500,
          }
        }}
      />
    </>
  );
};

export default BackpackModal;
